import { AttachmentList, collectAttachments } from './attachment-list'
import { createInlineImageNode, createInlineNodeRef, readInlineImageNode } from './prompt-input-image'
import { mergeAttachmentsIntoSegments } from '@/lib/message-segment-builders'
import { createPastedTextAttachment, isLongPaste } from '@/lib/pasted-text-attachment'
import { useState, useRef, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef, type ReactNode } from 'react'
import { ArrowUp, ListPlus, Mic, MicOff, Square, Loader2, Paperclip, Copy, Check, GitFork, GitBranch, ChevronUp } from 'lucide-react'
import { getIconForFile, getIconForFolder, DEFAULT_FILE, DEFAULT_FOLDER } from 'vscode-icons-js'
import { Button } from '@/components/ui/button'
import { TooltipProvider, TooltipHint } from '@/components/ui/tooltip'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Kbd } from '@/components/ui/kbd'
import { ModeSelector } from '@/components/chat/mode-selector'
import type { ChatMode } from '@/components/chat/mode-selector'
import { StyleSelector } from '@/components/chat/style-selector'
import { SendTargetSelector, type SendTarget } from '@/components/chat/send-target-selector'
import { ProviderModelSelector } from '@/components/chat/provider-model-selector'
import { ProviderRuntimeSelector } from '@/components/chat/provider-runtime-selector'
import type { ProviderId, RuntimeMode } from '@/lib/agents-api'
import type { RepositoryRuntimeInfo } from '@/lib/automation-repositories'
import type { SessionInfo, ChatAttachment } from '@/hooks/useChat'
import { FileIcon, FolderIcon } from '@/components/icons/file-icons'
import { useIsMobile } from '@/hooks/useIsMobile'
import type { SessionReasoningEffort } from '@/lib/session-chat-selection'
import { cn } from '@/lib/utils'
import { alternateStreamingAction, resolvePromptSubmitAction, type DefaultStreamingAction } from '@/lib/prompt-submit-routing'
import { JAIT_TERMINAL_REF_MIME, JAIT_PROJECT_REF_MIME, JAIT_CHAT_REF_MIME } from '@/lib/jait-dnd'
import { getAttachmentDraftForKey, persistAttachmentDraft } from '@/lib/prompt-input-attachment-draft'
import {
  JAIT_REF_MIME,
  formatLineRange,
  normalizeUserMessageSegments,
  parseUserMessageClipboardPayload,
  parseUserMessageMarkdown,
  serializeUserMessageSegmentsForClipboard,
  serializeUserMessageSegmentsToMarkdown,
  type UserMessageSegment,
  type UserTerminalReference,
  type UserProjectReference,
  type UserSkillReference,
  type UserChatReference,
  type UserImageAttachment,
} from '@/lib/user-message-segments'
import { getPromptDraftSignature, shouldSyncComposerDraft } from '@/lib/prompt-input-draft'
import { getRootCaretOffsetAfterChipRemoval, shouldRemovePreviousChipOnBackspace } from './prompt-input-selection'
import type { ResponseStyle } from '@jait/shared'

const ICON_CDN = 'https://cdn.jsdelivr.net/gh/vscode-icons/vscode-icons@12.9.0/icons/'

/** Stable empty array so the default prop doesn't create a new reference each render. */
const EMPTY_FILES: ReferencedFile[] = []
/** Stable empty array for the default skills prop. */
const EMPTY_SKILLS: PromptSkill[] = []

export interface ReferencedFile {
  path: string
  name: string
  kind?: 'file' | 'dir'
  lineRange?: { startLine: number; endLine: number }
}

/** A skill available for `/` slash-command invocation in the composer. */
export interface PromptSkill {
  id: string
  name: string
  description: string
}

type PromptChipReference =
  | ReferencedFile
  | ({ type: 'project' } & UserProjectReference)
  | ({ type: 'terminal' } & UserTerminalReference)
  | ({ type: 'chat' } & UserChatReference)
  | ({ type: 'skill' } & UserSkillReference)

interface PromptInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (chipFiles?: ReferencedFile[], attachments?: ChatAttachment[], segments?: UserMessageSegment[]) => void
  onStop?: () => void
  /** Queue a message while the agent is busy. */
  onQueue?: (chipFiles?: ReferencedFile[], attachments?: ChatAttachment[], segments?: UserMessageSegment[]) => void
  /** Steer the running agent with the currently typed content (applied immediately while streaming). */
  onSteer?: (chipFiles?: ReferencedFile[], attachments?: ChatAttachment[], segments?: UserMessageSegment[]) => void
  /** Fork the running session and ask the typed content as a parallel question branch. */
  onAskInParallel?: (chipFiles?: ReferencedFile[], attachments?: ChatAttachment[], segments?: UserMessageSegment[]) => void
  /** Submit the typed content into a fresh thread (used when the streaming default action is "thread"). */
  onThreadSubmit?: (chipFiles?: ReferencedFile[], attachments?: ChatAttachment[], segments?: UserMessageSegment[]) => void
  isLoading?: boolean
  /** Locks just the submit button and shows a spinner while chat state is still becoming ready. */
  submitLoading?: boolean
  disabled?: boolean
  controlsDisabled?: boolean
  placeholder?: string
  className?: string
  footerLeadingContent?: ReactNode
  footerTrailingContent?: ReactNode
  onVoiceInput?: () => void
  /** True while mic is actively recording */
  voiceRecording?: boolean
  /** Smoothed waveform values for the live mic preview */
  voiceLevels?: number[]
  /** True while audio is being transcribed */
  voiceTranscribing?: boolean
  /** Called when user clicks "Done" to stop recording */
  onVoiceStop?: () => void
  mode?: ChatMode
  onModeChange?: (mode: ChatMode) => void
  sendTarget?: SendTarget
  onSendTargetChange?: (target: SendTarget) => void
  showSendTargetSelector?: boolean
  /** User-configured default action for Enter/send while a session is streaming. */
  defaultStreamingAction?: DefaultStreamingAction
  provider?: ProviderId
  onProviderChange?: (provider: ProviderId) => void
  responseStyle?: ResponseStyle
  onResponseStyleChange?: (style: ResponseStyle) => void
  providerRuntimeMode?: RuntimeMode
  onProviderRuntimeModeChange?: (mode: RuntimeMode) => void
  /** Model override for CLI providers (codex / claude-code). */
  cliModel?: string | null
  onCliModelChange?: (model: string | null) => void
  reasoningEffort?: SessionReasoningEffort | null
  onReasoningEffortChange?: (reasoningEffort: SessionReasoningEffort | null) => void
  /** Scoped runtime info for the selected repo (Manager mode). */
  repoRuntime?: RepositoryRuntimeInfo | null
  /** Called when user wants to move the repo to the gateway. */
  onMoveToGateway?: () => void
  /** Active session info — shows where the current session is running. */
  sessionInfo?: SessionInfo | null
  /** Node ID of the open project (scopes CLI providers to that device). */
  projectNodeId?: string
  /** Project ID used to restore project-scoped model preferences. */
  projectId?: string | null
  /** Active chat/thread id — surfaced via a subtle copy button for quick handoff. */
  chatId?: string | null
  /** Display name for the currently selected project. */
  projectName?: string | null
  /** Full path for the currently selected project, used as hover context. */
  projectPath?: string | null
  /** All files available for @ mention (pre-loaded from visible tree) */
  availableFiles?: ReferencedFile[]
  /** Ordered text/file segments for restoring an existing draft. */
  segments?: UserMessageSegment[]
  /** Attachments shown when the composer first mounts (used when editing a sent message). */
  initialAttachments?: ChatAttachment[]
  /** Lazy search across the entire project directory */
  onSearchFiles?: (query: string, limit: number, signal?: AbortSignal) => Promise<ReferencedFile[]>
  /** Whether a project directory is currently open — @ mentions only work when true */
  projectOpen?: boolean
  /** Installed/enabled skills offered by the `/` slash-command menu. */
  availableSkills?: PromptSkill[]
  /** Stable key for preserving local attachment draft state across remounts. */
  draftStateKey?: string
  /** Bumped when the parent externally changes `value` (e.g. clear on submit). */
  syncKey?: number
  /** When true the input sits inside a shared composer surface and should not draw its own outer border. */
  merged?: boolean
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Build a non-editable inline chip DOM node for a file reference. */
function getChipRefKey(ref: PromptChipReference): string {
  const lineRange = 'lineRange' in ref ? ref.lineRange : undefined
  const rangeKey = lineRange ? `:L${lineRange.startLine}-L${lineRange.endLine}` : ''
  if ('type' in ref && ref.type === 'project') return `project:${ref.path}`
  if ('type' in ref && ref.type === 'terminal') {
    const selectionKey = ref.selectedText ? `:${hashString(ref.selectedText)}` : ''
    return `terminal:${ref.terminalId}${rangeKey}${selectionKey}`
  }
  if ('type' in ref && ref.type === 'chat') return `chat:${ref.sessionId}`
  if ('type' in ref && ref.type === 'skill') return `skill:${ref.id}`
  return `file:${ref.path}${rangeKey}`
}

/** Normalize a skill id/name to a `/slash-token` (kebab-case, matches gateway). */
function skillSlug(value: string): string {
  return value.toLowerCase().trim().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '')
}

function hashString(value: string): string {
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(i)
  }
  return (hash >>> 0).toString(36)
}

function createChipNode(file: PromptChipReference | ({ type: 'image' } & UserImageAttachment), onRemove?: (refKey: string) => void): HTMLSpanElement {
  if ('type' in file && file.type === 'image') return createInlineImageNode(file, onRemove ?? (() => {}))
  const chip = document.createElement('span')
  chip.contentEditable = 'false'
  const refKey = getChipRefKey(file)
  chip.setAttribute('data-chip-ref', refKey)
  if ('type' in file && file.type === 'project') {
    chip.setAttribute('data-segment-type', 'project')
    chip.setAttribute('data-project-path', file.path)
    chip.setAttribute('data-chip-name', file.name)
  } else if ('type' in file && file.type === 'terminal') {
    chip.setAttribute('data-segment-type', 'terminal')
    chip.setAttribute('data-terminal-id', file.terminalId)
    chip.setAttribute('data-chip-name', file.name)
    if (file.projectRoot) chip.setAttribute('data-project-root', file.projectRoot)
    if (file.lineRange) {
      chip.setAttribute('data-line-start', String(file.lineRange.startLine))
      chip.setAttribute('data-line-end', String(file.lineRange.endLine))
    }
    if (file.selectedText) chip.setAttribute('data-selected-text', file.selectedText)
  } else if ('type' in file && file.type === 'chat') {
    chip.setAttribute('data-segment-type', 'chat')
    chip.setAttribute('data-chat-id', file.sessionId)
    chip.setAttribute('data-chip-name', file.name)
  } else if ('type' in file && file.type === 'skill') {
    chip.setAttribute('data-segment-type', 'skill')
    chip.setAttribute('data-skill-id', file.id)
    chip.setAttribute('data-chip-name', file.name)
  } else {
    chip.setAttribute('data-segment-type', 'file')
    chip.setAttribute('data-file-path', file.path)
    chip.setAttribute('data-chip-name', file.name)
    if (file.kind) chip.setAttribute('data-kind', file.kind)
    if (file.lineRange) {
      chip.setAttribute('data-line-start', String(file.lineRange.startLine))
      chip.setAttribute('data-line-end', String(file.lineRange.endLine))
    }
  }
  chip.className =
    'inline-flex items-center gap-1.5 align-middle text-xs font-medium leading-none mx-[2px] rounded-md border border-border/70 bg-muted/45 pl-2 pr-1 py-1 text-foreground cursor-default whitespace-nowrap transition-colors'

  // Icon (file or folder)
  const icon = document.createElement('span')
  icon.className = 'inline-flex items-center shrink-0'
  if ('type' in file && file.type === 'project') {
    icon.innerHTML = `<img src="${ICON_CDN}${DEFAULT_FOLDER}" alt="" class="h-3.5 w-3.5" draggable="false" />`
  } else if ('type' in file && file.type === 'terminal') {
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5 text-muted-foreground"><path d="M4 17l6-6-6-6"/><path d="M12 19h8"/></svg>'
  } else if ('type' in file && file.type === 'chat') {
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5 text-muted-foreground"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"/></svg>'
  } else if ('type' in file && file.type === 'skill') {
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5 text-muted-foreground"><path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/></svg>'
  } else {
    const iconName = file.kind === 'dir' ? getVsFolderIconName(file.name) : getVsIconName(file.name)
    icon.innerHTML = `<img src="${ICON_CDN}${iconName}" alt="" class="h-3.5 w-3.5" draggable="false" />`
  }
  chip.appendChild(icon)

  // Name label
  const label = document.createElement('span')
  label.className = 'truncate max-w-[180px]'
  const lineRangeLabel = 'lineRange' in file ? formatLineRange(file.lineRange) : ''
  label.textContent = 'type' in file && file.type === 'skill'
    ? `/${file.name}`
    : lineRangeLabel ? `${file.name}:${lineRangeLabel.replace(/^lines? /, '')}` : file.name
  chip.appendChild(label)

  // Remove button
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'inline-flex items-center p-0.5 rounded hover:bg-foreground/10 transition-colors text-muted-foreground hover:text-foreground'
  btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
  btn.addEventListener('mousedown', (e) => {
    e.preventDefault()
    e.stopPropagation()
  })
  btn.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (onRemove) onRemove(refKey)
    else chip.remove()
  })
  chip.appendChild(btn)

  return chip
}

/** Simple extension→icon filename (reuses the CDN approach from file-icons.tsx). */
function getVsIconName(filename: string): string {
  return getIconForFile(filename) ?? DEFAULT_FILE
}

/** Folder name→icon filename. */
function getVsFolderIconName(foldername: string): string {
  return getIconForFolder(foldername) ?? DEFAULT_FOLDER
}

/** Extract plain text from the editable div, ignoring chip nodes. */
function getTextFromEditable(el: HTMLElement): string {
  let text = ''
  const children = el.childNodes
  for (let i = 0; i < children.length; i++) {
    const node = children[i]
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? ''
    } else if (node instanceof HTMLElement) {
      if (node.hasAttribute('data-chip-ref')) {
        // Skip chips — they're represented separately
        continue
      } else if (node.tagName === 'BR') {
        // Ignore trailing <br> that browsers insert in empty/end-of contentEditable
        const isLast = i === children.length - 1
        if (!isLast) text += '\n'
      } else {
        text += getTextFromEditable(node)
      }
    }
  }
  return text
}

/** Strip zero-width and invisible formatting characters. */
function stripInvisible(s: string): string {
  return s.replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
}

/** Check if a text string is empty (ignoring only zero-width chars). */
function isTextEmpty(s: string): boolean {
  return stripInvisible(s).length === 0
}

/** Get file paths from all chip nodes in the editable div. */
function getChipPaths(el: HTMLElement): string[] {
  const paths: string[] = []
  el.querySelectorAll('[data-segment-type="file"][data-file-path]').forEach((chip) => {
    paths.push(chip.getAttribute('data-file-path')!)
  })
  return paths
}

/** Get all chip file references from the editable div. */
function getChipFiles(el: HTMLElement): ReferencedFile[] {
  const files: ReferencedFile[] = []
  el.querySelectorAll('[data-segment-type="file"][data-file-path]').forEach((chip) => {
    const kind = chip.getAttribute('data-kind')
    files.push({
      path: chip.getAttribute('data-file-path')!,
      name: chip.getAttribute('data-chip-name') || chip.getAttribute('data-file-path')!.split(/[\\/]/).pop()!,
      ...(kind === 'file' || kind === 'dir' ? { kind } : {}),
      ...readChipLineRange(chip as HTMLElement),
    })
  })
  return files
}

function getComposerSegments(el: HTMLElement): UserMessageSegment[] {
  const segments: UserMessageSegment[] = []

  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? ''
      if (text) segments.push({ type: 'text', text })
      return
    }

    if (!(node instanceof HTMLElement)) return

    if (node.hasAttribute('data-chip-ref')) {
      const segmentType = node.getAttribute('data-segment-type')
      if (segmentType === 'image') {
        const image = readInlineImageNode(node)
        if (image) segments.push(image)
        return
      }
      if (segmentType === 'project') {
        const path = node.getAttribute('data-project-path')
        if (!path) return
        segments.push({
          type: 'project',
          path,
          name: node.getAttribute('data-chip-name') || path.split(/[\\/]/).pop() || path,
        })
        return
      }
      if (segmentType === 'skill') {
        const id = node.getAttribute('data-skill-id')
        if (!id) return
        segments.push({
          type: 'skill',
          id,
          name: node.getAttribute('data-chip-name') || id,
        })
        return
      }
      if (segmentType === 'terminal') {
        const terminalId = node.getAttribute('data-terminal-id')
        if (!terminalId) return
        const projectRoot = node.getAttribute('data-project-root')
        segments.push({
          type: 'terminal',
          terminalId,
          name: node.getAttribute('data-chip-name') || terminalId,
          ...(projectRoot ? { projectRoot } : {}),
          ...readChipLineRange(node),
          ...(node.getAttribute('data-selected-text') ? { selectedText: node.getAttribute('data-selected-text')! } : {}),
        })
        return
      }
      if (segmentType === 'chat') {
        const sessionId = node.getAttribute('data-chat-id')
        if (!sessionId) return
        segments.push({
          type: 'chat',
          sessionId,
          name: node.getAttribute('data-chip-name') || sessionId,
        })
        return
      }
      const path = node.getAttribute('data-file-path')
      if (!path) return
      const kind = node.getAttribute('data-kind')
      segments.push({
        type: 'file',
        path,
        name: node.getAttribute('data-chip-name') || path.split(/[\\/]/).pop() || path,
        ...(kind === 'file' || kind === 'dir' ? { kind } : {}),
        ...readChipLineRange(node),
      })
      return
    }

    if (node.tagName === 'BR') {
      segments.push({ type: 'text', text: '\n' })
      return
    }

    for (const child of node.childNodes) visit(child)
  }

  for (const child of el.childNodes) visit(child)
  return normalizeUserMessageSegments(segments)
}

function readChipLineRange(node: HTMLElement): { lineRange: { startLine: number; endLine: number } } | Record<string, never> {
  const startLine = Number.parseInt(node.getAttribute('data-line-start') ?? '', 10)
  const endLine = Number.parseInt(node.getAttribute('data-line-end') ?? '', 10)
  if (!Number.isFinite(startLine) || !Number.isFinite(endLine) || startLine < 1 || endLine < startLine) return {}
  return { lineRange: { startLine, endLine } }
}

function hasChipRefs(el: HTMLElement | null): boolean {
  return Boolean(el?.querySelector('[data-chip-ref]'))
}

function restoreCaretAfterChipRemoval(root: HTMLElement, nextSibling: Node | null, childIndex: number) {
  const selection = window.getSelection()
  if (!selection) return

  const nextRange = document.createRange()
  if (nextSibling?.nodeType === Node.TEXT_NODE) {
    nextRange.setStart(nextSibling, 0)
  } else if (nextSibling?.parentNode === root) {
    nextRange.setStart(root, Array.from(root.childNodes).indexOf(nextSibling as ChildNode))
  } else {
    nextRange.setStart(root, getRootCaretOffsetAfterChipRemoval(childIndex, root.childNodes.length))
  }
  nextRange.collapse(true)
  selection.removeAllRanges()
  selection.addRange(nextRange)
}

function restoreCaretAfterChipRemovalStable(root: HTMLElement, nextSibling: Node | null, childIndex: number) {
  restoreCaretAfterChipRemoval(root, nextSibling, childIndex)
  requestAnimationFrame(() => {
    if (!root.isConnected) return
    restoreCaretAfterChipRemoval(root, nextSibling, childIndex)
  })
}

function buildEditableContent(
  el: HTMLElement,
  segments: UserMessageSegment[] | undefined,
  fallbackValue: string,
  onRemove: (refKey: string) => void,
) {
  el.innerHTML = ''

  const normalized = normalizeUserMessageSegments(segments)
  if (normalized.length === 0) {
    if (fallbackValue) el.appendChild(document.createTextNode(fallbackValue))
    return
  }

  for (const segment of normalized) {
    appendSegmentNode(el, segment, onRemove)
  }
}

function appendSegmentNode(
  parent: Node,
  segment: UserMessageSegment,
  onRemove: (refKey: string) => void,
) {
  if (segment.type === 'text') {
    parent.appendChild(document.createTextNode(segment.text))
    return
  }
  if (segment.type === 'file' || segment.type === 'project' || segment.type === 'terminal' || segment.type === 'skill' || segment.type === 'chat' || segment.type === 'image') {
    parent.appendChild(createChipNode(segment, onRemove))
    return
  }
}

function insertSegmentsAtCursor(
  el: HTMLElement,
  segments: UserMessageSegment[],
  onRemove: (refKey: string) => void,
) {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !el.contains(selection.getRangeAt(0).commonAncestorContainer)) {
    for (const segment of segments) {
      appendSegmentNode(el, segment, onRemove)
    }
    moveCursorToEnd(el)
    return
  }

  const range = selection.getRangeAt(0)
  range.deleteContents()

  const fragment = document.createDocumentFragment()
  for (const segment of segments) {
    appendSegmentNode(fragment, segment, onRemove)
  }

  const lastNode = fragment.lastChild
  range.insertNode(fragment)

  if (!lastNode) return

  const nextRange = document.createRange()
  if (lastNode.nodeType === Node.TEXT_NODE) {
    nextRange.setStart(lastNode, lastNode.textContent?.length ?? 0)
  } else {
    nextRange.setStartAfter(lastNode)
  }
  nextRange.collapse(true)
  selection.removeAllRanges()
  selection.addRange(nextRange)
}

function insertTextAtCursor(el: HTMLElement, text: string) {
  if (!text) return

  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    el.appendChild(document.createTextNode(text))
    moveCursorToEnd(el)
    return
  }

  const range = selection.getRangeAt(0)
  const ancestor = range.commonAncestorContainer
  if (ancestor !== el && !el.contains(ancestor)) {
    el.appendChild(document.createTextNode(text))
    moveCursorToEnd(el)
    return
  }

  range.deleteContents()
  const textNode = document.createTextNode(text)
  range.insertNode(textNode)

  const nextRange = document.createRange()
  nextRange.setStart(textNode, textNode.length)
  nextRange.collapse(true)
  selection.removeAllRanges()
  selection.addRange(nextRange)
}

/** Move cursor to the end of a contentEditable element. */
function moveCursorToEnd(el: HTMLElement) {
  const sel = window.getSelection()
  if (sel) {
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    sel.removeAllRanges()
    sel.addRange(range)
  }
}

function scrollCaretIntoView(el: HTMLElement) {
  window.requestAnimationFrame(() => {
    if (!el.isConnected) return
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      el.scrollTop = el.scrollHeight
      return
    }
    const range = selection.getRangeAt(0)
    const ancestor = range.commonAncestorContainer
    if (ancestor !== el && !el.contains(ancestor)) return

    const rect = range.getClientRects()[0] ?? range.getBoundingClientRect()
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      el.scrollTop = el.scrollHeight
      return
    }

    const containerRect = el.getBoundingClientRect()
    const padding = 8
    if (rect.bottom > containerRect.bottom - padding) {
      el.scrollTop += rect.bottom - containerRect.bottom + padding
    } else if (rect.top < containerRect.top + padding) {
      el.scrollTop -= containerRect.top + padding - rect.top
    }
  })
}

/** Remove empty text nodes and stale <br> tags so chips aren't offset. */
function cleanEmptyNodes(el: HTMLElement) {
  for (let i = el.childNodes.length - 1; i >= 0; i--) {
    const child = el.childNodes[i]
    if (child instanceof HTMLElement && child.tagName === 'BR') {
      child.remove()
    } else if (child.nodeType === Node.TEXT_NODE && !child.textContent) {
      child.remove()
    }
  }
}

function sanitizeEditableContent(el: HTMLElement) {
  for (let i = el.childNodes.length - 1; i >= 0; i--) {
    const child = el.childNodes[i]
    if (child.nodeType === Node.TEXT_NODE) continue
    if (!(child instanceof HTMLElement)) {
      child.remove()
      continue
    }
    if (child.hasAttribute('data-chip-ref') || child.tagName === 'BR') continue
    if (child.matches('button, a, input, textarea, select, svg, [role="button"]')
      || child.querySelector('button, a, input, textarea, select, svg, [role="button"]')) {
      child.remove()
      continue
    }
    child.replaceWith(document.createTextNode(child.textContent ?? ''))
  }
}

function appendSegmentNodes(
  el: HTMLElement,
  segments: UserMessageSegment[],
  onRemoveChip?: (refKey: string) => void,
) {
  cleanEmptyNodes(el)
  for (const segment of normalizeUserMessageSegments(segments)) {
    if (segment.type === 'text') {
      if (segment.text) el.appendChild(document.createTextNode(segment.text))
      continue
    }
    if (segment.type === 'attachment') continue
    if (segment.type !== 'image' && el.querySelector(`[data-chip-ref="${CSS.escape(getChipRefKey(segment))}"]`)) continue
    el.appendChild(createChipNode(segment, onRemoveChip))
    el.appendChild(document.createTextNode(' '))
  }
}

export interface PromptInputHandle {
  /** Insert a file chip into the input (used by project Send button). */
  insertChip: (file: ReferencedFile) => void
  /** Insert text and/or structured references into the composer. */
  insertSegments: (segments: UserMessageSegment[]) => void
  /** Read the current ordered text/file segments from the composer. */
  getSegments: () => UserMessageSegment[]
  /** Focus the editor and move the caret to the end. */
  focus: () => void
  /** Append a file attachment (binary/text) directly to the attachments list. */
  addAttachment: (attachment: import('@/hooks/useChat').ChatAttachment) => void
}

const attachmentDraftStore = new Map<string, ChatAttachment[]>()
const COMPACT_FOOTER_CONTROLS_WIDTH = 560

export function VoiceLevelMeter({ levels = [], compact = false }: { levels?: number[]; compact?: boolean }) {
  const bars = levels.length > 0 ? levels : Array.from({ length: 28 }, () => 0.05)
  const visibleBars = compact ? bars.slice(-16) : bars
  const meterHeight = compact ? 24 : 30

  return (
    <span
      className={cn(
        'flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden rounded-full border border-black/10 bg-gradient-to-b from-black/5 via-black/10 to-black/5 px-3 py-1 dark:border-white/10 dark:from-white/5 dark:via-white/10 dark:to-white/5',
        compact ? 'h-8 max-w-[210px]' : 'h-10 max-w-[320px]',
      )}
    >
      {visibleBars.map((level, index) => (
        <span
          key={index}
          className={cn(
            'block shrink-0 rounded-full bg-black/75 transition-[height,opacity,transform] duration-300 ease-out dark:bg-white/80',
            compact ? 'w-[2px]' : 'w-[3px]',
          )}
          style={{
            height: `${6 + level * meterHeight}px`,
            opacity: 0.18 + level * 0.82,
            transform: `translateY(${(1 - level) * 1.5}px)`,
          }}
        />
      ))}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/* Send button (primary action + options)                              */
/* ------------------------------------------------------------------ */

/** Everything the composer can do with its content while a session streams. */
type ComposerSendAction = 'steer' | 'queue' | 'thread' | 'ask-in-parallel'

interface ComposerSendOption {
  id: ComposerSendAction
  /** Full sentence used as the primary button's aria-label and tooltip. */
  tooltip: string
  /** Short label shown inside the send-options menu. */
  name: string
  /** One-line explanation shown inside the send-options menu. */
  description: string
  /** Keycap hint rendered next to the menu entry. */
  shortcut?: readonly string[]
  icon: ReactNode
}

const COMPOSER_SEND_META: Record<ComposerSendAction, Omit<ComposerSendOption, 'id'>> = {
  steer: {
    tooltip: 'Steer the running agent',
    name: 'Steer',
    description: 'Send this into the response that is streaming right now',
    icon: <ArrowUp className="h-4 w-4" />,
  },
  queue: {
    tooltip: 'Add to queue',
    name: 'Queue',
    description: 'Send this as soon as the current response finishes',
    icon: <ListPlus className="h-4 w-4" />,
  },
  thread: {
    tooltip: 'Send as new thread',
    name: 'New thread',
    description: 'Move this message into its own thread',
    icon: <GitBranch className="h-4 w-4" />,
  },
  'ask-in-parallel': {
    tooltip: 'Ask in parallel',
    name: 'Ask in parallel',
    description: 'Fork the session and answer this as a side branch',
    icon: <GitFork className="h-4 w-4" />,
  },
}

/**
 * Send button for a streaming session: the primary action (queue by default)
 * plus a caret that opens the remaining send options, e.g. steer.
 */
function ComposerSendSplitButton({
  primary,
  options,
  disabled,
  loading = false,
  onAction,
}: {
  primary: ComposerSendOption
  options: ComposerSendOption[]
  disabled?: boolean
  /** Show a spinner instead of the icon while the chat is still starting. */
  loading?: boolean
  onAction: (action: ComposerSendAction) => void
}) {
  const blocked = Boolean(disabled) || loading
  return (
    <TooltipProvider>
      <div className="flex shrink-0 items-stretch">
        <TooltipHint side="bottom" content={`${primary.tooltip} (Enter)`}>
          <Button
            type="button"
            size="icon"
            className={cn('shrink-0 rounded-lg', options.length > 0 && 'rounded-r-none')}
            disabled={blocked}
            aria-label={`${primary.tooltip} (Enter)`}
            onClick={() => onAction(primary.id)}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : primary.icon}
          </Button>
        </TooltipHint>
        {options.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <TooltipHint side="bottom" content="More send options">
                <Button
                  type="button"
                  size="icon"
                  className="-ml-px w-7 shrink-0 rounded-l-none rounded-r-lg px-0"
                  disabled={disabled}
                  aria-label="More send options"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
              </TooltipHint>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" sideOffset={8} className="w-72">
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  While the agent is running
                </DropdownMenuLabel>
                {options.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    className="items-start gap-2.5"
                    onSelect={() => onAction(option.id)}
                  >
                    <span className="mt-0.5 shrink-0 text-muted-foreground">{option.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2 text-sm font-medium">
                        <span className="truncate">{option.name}</span>
                        {option.shortcut && <Kbd keys={option.shortcut} />}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{option.description}</span>
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
        )}
      </div>
    </TooltipProvider>
  )
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const PromptInput = forwardRef<PromptInputHandle, PromptInputProps>(function PromptInput({
  value,
  onChange,
  onSubmit,
  onStop,
  onQueue,
  onSteer,
  onAskInParallel,
  onThreadSubmit,
  isLoading,
  submitLoading,
  disabled,
  controlsDisabled,
  placeholder = 'Ask anything...',
  className,
  footerLeadingContent,
  footerTrailingContent,
  onVoiceInput,
  voiceRecording,
  voiceLevels,
  voiceTranscribing,
  onVoiceStop,
  mode,
  onModeChange,
  sendTarget,
  onSendTargetChange,
  showSendTargetSelector = true,
  defaultStreamingAction,
  provider,
  onProviderChange,
  responseStyle,
  onResponseStyleChange,
  providerRuntimeMode,
  onProviderRuntimeModeChange,
  cliModel,
  onCliModelChange,
  reasoningEffort,
  onReasoningEffortChange,
  repoRuntime,
  onMoveToGateway,
  sessionInfo,
  projectNodeId,
  projectId,
  projectName,
  projectPath,
  chatId,
  availableFiles = EMPTY_FILES,
  segments,
  onSearchFiles,
  projectOpen = false,
  availableSkills = EMPTY_SKILLS,
  draftStateKey,
  initialAttachments,
  syncKey,
  merged,
}: PromptInputProps, ref) {
  const rootRef = useRef<HTMLDivElement>(null)
  const editableRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const [compactFooterControls, setCompactFooterControls] = useState(false)

  const [dragging, setDragging] = useState(false)
  const dragCounter = useRef(0)
  const [chatIdCopied, setChatIdCopied] = useState(false)
  const [isEmpty, setIsEmpty] = useState(isTextEmpty(value))
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [readingAttachments, setReadingAttachments] = useState(0)
  const [attachments, setAttachments] = useState<ChatAttachment[]>(
    () => initialAttachments ?? (draftStateKey ? attachmentDraftStore.get(draftStateKey) ?? [] : []),
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const draftSegmentsRef = useRef<UserMessageSegment[]>(normalizeUserMessageSegments(segments))
  const attachmentsRef = useRef(attachments)
  const previousDraftStateKeyRef = useRef(draftStateKey)
  const skipNextAttachmentPersistRef = useRef(false)
  const lastAppliedDraftSignatureRef = useRef<string | null>(null)

  // Undo/redo stack for the composer (prevents browser native undo corruption)
  const undoStackRef = useRef<UserMessageSegment[][]>([])
  const redoStackRef = useRef<UserMessageSegment[][]>([])
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // @ mention state
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)

  // `/` slash-command (skills) state
  const [slashOpen, setSlashOpen] = useState(false)
  const [slashQuery, setSlashQuery] = useState('')
  const [slashIndex, setSlashIndex] = useState(0)
  const slashMenuRef = useRef<HTMLDivElement>(null)

  // Keep a ref in sync so the onInput handler always reads the latest value
  const mentionOpenRef = useRef(false)
  useEffect(() => { mentionOpenRef.current = mentionOpen }, [mentionOpen])
  const slashOpenRef = useRef(false)
  useEffect(() => { slashOpenRef.current = slashOpen }, [slashOpen])
  const availableSkillsRef = useRef(availableSkills)
  useEffect(() => { availableSkillsRef.current = availableSkills }, [availableSkills])
  const availableFilesRef = useRef(availableFiles)
  useEffect(() => { availableFilesRef.current = availableFiles }, [availableFiles])
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])
  const onSearchFilesRef = useRef(onSearchFiles)
  useEffect(() => { onSearchFilesRef.current = onSearchFiles }, [onSearchFiles])
  const pushUndoRef = useRef<(immediate?: boolean) => void>(() => {})

  // Filtered skill list for the `/` slash menu (local, no async needed)
  const slashResults = useMemo(() => {
    const q = slashQuery.toLowerCase()
    const list = q
      ? availableSkills.filter((s) =>
          s.id.toLowerCase().includes(q) ||
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q))
      : availableSkills
    return list.slice(0, 20)
  }, [availableSkills, slashQuery])

  // Async search results for @ mention
  const [searchResults, setSearchResults] = useState<ReferencedFile[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const searchAbort = useRef<AbortController | null>(null)
  const composerDisabled = Boolean(disabled)
  const controlsLocked = Boolean(controlsDisabled ?? disabled ?? false)
  const selectorsDisabled = controlsLocked
  const showProviderModelSelector = Boolean(provider && onProviderChange && onCliModelChange)
  const showResponseStyleSelector = Boolean(responseStyle && onResponseStyleChange)
  const showProviderRuntimeSelector = Boolean(provider && providerRuntimeMode && onProviderRuntimeModeChange)
  const showModeSelector = Boolean(mode && onModeChange && sendTarget !== 'thread' && sendTarget !== 'swarm' && (!provider || provider === 'jait'))
  const shouldShowSendTargetSelector = showSendTargetSelector && Boolean(sendTarget && onSendTargetChange)
  const projectDisplayName = projectName?.trim() || projectPath?.trim() || null
  const hasFooterControls = shouldShowSendTargetSelector || showProviderModelSelector || showResponseStyleSelector || showProviderRuntimeSelector || showModeSelector || Boolean(footerLeadingContent)
  const hasFooterLeftContent = hasFooterControls
  const submitEmpty = (isEmpty && attachments.length === 0) || readingAttachments > 0
  const submitAction = resolvePromptSubmitAction({
    isLoading,
    sendTarget,
    hasQueueHandler: Boolean(onQueue),
    hasSteerHandler: Boolean(onSteer),
    hasThreadHandler: Boolean(onThreadSubmit),
    defaultAction: defaultStreamingAction,
  })
  const hasStreamingSendAction = Boolean(onSteer || onQueue || onThreadSubmit || onAskInParallel)
  const streamingSendPrimary = useMemo<ComposerSendOption>(() => {
    const id: ComposerSendAction =
      submitAction === 'steer' ? 'steer' : submitAction === 'thread' ? 'thread' : 'queue'
    return { id, ...COMPOSER_SEND_META[id] }
  }, [submitAction])
  const streamingSendOptions = useMemo<ComposerSendOption[]>(() => {
    const available: Record<ComposerSendAction, boolean> = {
      steer: Boolean(onSteer),
      queue: Boolean(onQueue),
      thread: Boolean(onThreadSubmit),
      'ask-in-parallel': Boolean(onAskInParallel),
    }
    const altAction = alternateStreamingAction(streamingSendPrimary.id === 'queue' ? 'queue' : 'steer')
    return (['queue', 'steer', 'thread', 'ask-in-parallel'] as ComposerSendAction[])
      .filter((id) => id !== streamingSendPrimary.id && available[id])
      .map((id) => ({
        id,
        ...COMPOSER_SEND_META[id],
        shortcut: id === altAction ? (['Alt', 'Enter'] as const) : undefined,
      }))
  }, [streamingSendPrimary.id, onSteer, onQueue, onThreadSubmit, onAskInParallel])

  useEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === 'undefined') {
      setCompactFooterControls(false)
      return
    }

    const updateCompact = () => {
      setCompactFooterControls(el.clientWidth < COMPACT_FOOTER_CONTROLS_WIDTH)
    }

    updateCompact()
    const observer = new ResizeObserver(() => updateCompact())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Track whether we're doing a controlled sync to avoid loops
  const isSyncing = useRef(false)

  useEffect(() => {
    attachmentsRef.current = attachments
  }, [attachments])

  useEffect(() => {
    const previousDraftStateKey = previousDraftStateKeyRef.current
    previousDraftStateKeyRef.current = draftStateKey
    const nextAttachments = initialAttachments ?? getAttachmentDraftForKey({
      store: attachmentDraftStore,
      draftStateKey,
      previousDraftStateKey,
      currentAttachments: attachmentsRef.current,
    })
    skipNextAttachmentPersistRef.current = true
    attachmentsRef.current = nextAttachments
    setAttachments(nextAttachments)
    lastAppliedDraftSignatureRef.current = null
    persistAttachmentDraft(attachmentDraftStore, draftStateKey, nextAttachments)
  }, [draftStateKey])

  useEffect(() => {
    if (skipNextAttachmentPersistRef.current) {
      skipNextAttachmentPersistRef.current = false
      return
    }
    persistAttachmentDraft(attachmentDraftStore, draftStateKey, attachments)
  }, [draftStateKey, attachments])

  const syncComposerSegments = useCallback((el: HTMLElement) => {
    const nextSegments = getComposerSegments(el)
    const nextAttachments = collectAttachments(attachmentsRef.current.filter(item => !item.mimeType.startsWith('image/')), nextSegments.filter(segment => segment.type === 'image'))
    const previous = attachmentsRef.current
    if (previous.length !== nextAttachments.length || previous.some((item, index) => item.name !== nextAttachments[index]?.name || item.data !== nextAttachments[index]?.data || item.mimeType !== nextAttachments[index]?.mimeType)) {
      attachmentsRef.current = nextAttachments
      setAttachments(nextAttachments)
    }
    return nextSegments
  }, [])

  /** Remove a chip from the editable DOM and update isEmpty. */
  const handleRemoveChip = useCallback((refKey: string) => {
    const el = editableRef.current
    if (!el) return
    pushUndoRef.current(true)
    el.querySelector(`[data-chip-ref="${CSS.escape(refKey)}"]`)?.remove()
    draftSegmentsRef.current = syncComposerSegments(el)
    isSyncing.current = true
    onChangeRef.current(getTextFromEditable(el))
    isSyncing.current = false
    setIsEmpty(isTextEmpty(getTextFromEditable(el)) && !hasChipRefs(el))
  }, [syncComposerSegments])

  const resetComposer = useCallback(() => {
    const el = editableRef.current
    if (!el) return
    buildEditableContent(el, [], '', handleRemoveChip)
    draftSegmentsRef.current = []
    lastAppliedDraftSignatureRef.current = getPromptDraftSignature('', [])
    isSyncing.current = true
    onChangeRef.current('')
    isSyncing.current = false
    setIsEmpty(true)
    undoStackRef.current = []
    redoStackRef.current = []
  }, [handleRemoveChip])

  /** Push a snapshot onto the undo stack (debounced for typing). */
  const pushUndoSnapshot = useCallback((immediate?: boolean) => {
    const push = () => {
      const snap = [...draftSegmentsRef.current]
      const stack = undoStackRef.current
      // Avoid duplicate consecutive snapshots
      if (stack.length > 0 && JSON.stringify(stack[stack.length - 1]) === JSON.stringify(snap)) return
      stack.push(snap)
      if (stack.length > 100) stack.shift()
      redoStackRef.current = []
    }
    if (immediate) {
      if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null }
      push()
    } else {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current)
      undoTimerRef.current = setTimeout(push, 300)
    }
  }, [])

  useEffect(() => { pushUndoRef.current = pushUndoSnapshot }, [pushUndoSnapshot])

  /** Restore a segments snapshot into the editable. */
  const restoreSnapshot = useCallback((snap: UserMessageSegment[]) => {
    const el = editableRef.current
    if (!el) return
    isSyncing.current = true
    buildEditableContent(el, snap, '', handleRemoveChip)
    draftSegmentsRef.current = syncComposerSegments(el)
    const text = getTextFromEditable(el)
    onChangeRef.current(text)
    setIsEmpty(isTextEmpty(text) && !hasChipRefs(el))
    isSyncing.current = false
    moveCursorToEnd(el)
  }, [handleRemoveChip, syncComposerSegments])

  /** Expose imperative methods for parent components. */
  useImperativeHandle(ref, () => ({
    insertChip: (file: ReferencedFile) => {
      const el = editableRef.current
      if (!el) return
      // Don't add duplicate
      if (el.querySelector(`[data-chip-ref="${CSS.escape(getChipRefKey(file))}"]`)) return
      pushUndoRef.current(true)
      cleanEmptyNodes(el)
      const chip = createChipNode(file, handleRemoveChip)
      el.appendChild(chip)
      el.appendChild(document.createTextNode(' '))
      draftSegmentsRef.current = syncComposerSegments(el)
      moveCursorToEnd(el)
      isSyncing.current = true
      onChangeRef.current(getTextFromEditable(el))
      isSyncing.current = false
      setIsEmpty(false)
    },
    insertSegments: (nextSegments: UserMessageSegment[]) => {
      const el = editableRef.current
      if (!el) return
      const normalized = normalizeUserMessageSegments(nextSegments)
      if (!normalized.length) return
      pushUndoRef.current(true)
      appendSegmentNodes(el, normalized, handleRemoveChip)
      draftSegmentsRef.current = syncComposerSegments(el)
      moveCursorToEnd(el)
      isSyncing.current = true
      onChangeRef.current(getTextFromEditable(el))
      isSyncing.current = false
      setIsEmpty(false)
    },
    getSegments: () => {
      const el = editableRef.current
      return el ? getComposerSegments(el) : normalizeUserMessageSegments(segments)
    },
    focus: () => {
      const el = editableRef.current
      if (!el) return
      el.focus()
      moveCursorToEnd(el)
    },
    addAttachment: (attachment) => {
      const el = editableRef.current
      if (el && attachment.mimeType.startsWith('image/')) {
        pushUndoRef.current(true)
        el.appendChild(createInlineImageNode(attachment, handleRemoveChip))
        draftSegmentsRef.current = syncComposerSegments(el)
        isSyncing.current = true
        onChangeRef.current(getTextFromEditable(el))
        isSyncing.current = false
      } else {
        setAttachments(prev => [...prev, attachment])
      }
      setIsEmpty(false)
    },
  }), [handleRemoveChip, segments, syncComposerSegments])

  // Debounced search when mention query changes
  useEffect(() => {
    if (!mentionOpen) {
      // Functional updater: skip re-render when already empty
      setSearchResults(prev => prev.length === 0 ? prev : EMPTY_FILES)
      return
    }

    // Cancel any in-flight search
    searchAbort.current?.abort()

    const alreadyReferenced = new Set(
      editableRef.current ? getChipPaths(editableRef.current) : []
    )

    // Read from ref so availableFiles isn't in the deps array
    const currentFiles = availableFilesRef.current

    // For empty query, show pre-loaded files (top-level from tree)
    if (!mentionQuery) {
      const quick = currentFiles
        .filter((f) => !alreadyReferenced.has(f.path))
        .slice(0, 15)
      setSearchResults(quick)
      setSearchLoading(false)
      return
    }

    // First show instant results from already-loaded list
    const instant = currentFiles
      .filter((f) => {
        if (alreadyReferenced.has(f.path)) return false
        const q = mentionQuery.toLowerCase()
        return f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q)
      })
      .slice(0, 10)
    setSearchResults(instant)

    // Then do a lazy FS search if onSearchFiles is provided
    const searchFn = onSearchFilesRef.current
    if (!searchFn) {
      setSearchLoading(false)
      return
    }

    setSearchLoading(true)
    const controller = new AbortController()
    searchAbort.current = controller

    const timer = setTimeout(async () => {
      try {
        const results = await searchFn(mentionQuery, 15, controller.signal)
        if (controller.signal.aborted) return
        // Merge: FS results first, deduped, excluding already-referenced
        const seen = new Set<string>()
        const merged: ReferencedFile[] = []
        for (const r of results) {
          if (alreadyReferenced.has(r.path) || seen.has(r.path)) continue
          seen.add(r.path)
          merged.push(r)
        }
        // Add any from instant that weren't in FS results
        for (const r of instant) {
          if (!seen.has(r.path)) {
            seen.add(r.path)
            merged.push(r)
          }
        }
        setSearchResults(merged.slice(0, 15))
      } catch {
        // search cancelled or error — keep instant results
      } finally {
        if (!controller.signal.aborted) setSearchLoading(false)
      }
    }, 150) // 150ms debounce

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionOpen, mentionQuery])

  // Sync external value → contentEditable (only when value changes externally, e.g. cleared on submit)
  useEffect(() => {
    const el = editableRef.current
    if (!el || isSyncing.current) return

    const hasRenderedContent = el.childNodes.length > 0
      && (((el.textContent ?? '').length > 0) || hasChipRefs(el))

    if (hasRenderedContent && !shouldSyncComposerDraft(lastAppliedDraftSignatureRef.current, value, segments, draftSegmentsRef.current)) {
      lastAppliedDraftSignatureRef.current = getPromptDraftSignature(value, segments)
      return
    }

    isSyncing.current = true
    const restoredSegments = lastAppliedDraftSignatureRef.current === null
      ? mergeAttachmentsIntoSegments(segments?.length ? segments : [{ type: 'text', text: value }], attachmentsRef.current.filter(item => item.mimeType.startsWith('image/')))
      : segments
    buildEditableContent(el, restoredSegments, value, handleRemoveChip)
    draftSegmentsRef.current = syncComposerSegments(el)
    setIsEmpty(isTextEmpty(value) && !hasChipRefs(editableRef.current))
    isSyncing.current = false
    lastAppliedDraftSignatureRef.current = getPromptDraftSignature(value, segments)
  }, [value, segments, handleRemoveChip, syncKey, draftStateKey, syncComposerSegments])

  // Close menu on outside click
  useEffect(() => {
    if (!mentionOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMentionOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [mentionOpen])

  useEffect(() => {
    setMentionIndex(0)
  }, [mentionQuery])

  useEffect(() => {
    if (!mentionOpen) return
    const frame = requestAnimationFrame(() => {
      menuRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(frame)
  }, [mentionOpen, mentionIndex, searchResults.length])

  useEffect(() => {
    setSlashIndex(0)
  }, [slashQuery])

  useEffect(() => {
    if (!slashOpen) return
    const frame = requestAnimationFrame(() => {
      slashMenuRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(frame)
  }, [slashOpen, slashIndex, slashResults.length])

  // Close slash menu on outside click
  useEffect(() => {
    if (!slashOpen) return
    const handler = (e: MouseEvent) => {
      if (slashMenuRef.current && !slashMenuRef.current.contains(e.target as Node)) {
        setSlashOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [slashOpen])

  /** Insert a file chip at the current cursor position and close the menu. */
  const insertMention = useCallback((file: ReferencedFile) => {
    const el = editableRef.current
    if (!el) return
    pushUndoSnapshot(true)

    // Remove the @query text
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      // Walk backwards to find and remove the @query
      const textNode = range.startContainer
      if (textNode.nodeType === Node.TEXT_NODE && textNode.textContent) {
        const text = textNode.textContent
        const cursor = range.startOffset
        const atIdx = text.lastIndexOf('@', cursor - 1)
        if (atIdx >= 0) {
          textNode.textContent = text.slice(0, atIdx) + text.slice(cursor)
          // Insert chip at atIdx position
          const before = textNode.textContent.slice(0, atIdx)
          const after = textNode.textContent.slice(atIdx)
          textNode.textContent = before

          const chip = createChipNode(file, handleRemoveChip)
          const afterNode = document.createTextNode(after || ' ')

          const parent = textNode.parentNode!

          // Remove the text node if it's now empty to avoid phantom whitespace
          if (!before) {
            const ref = textNode.nextSibling
            parent.removeChild(textNode)
            if (ref) {
              parent.insertBefore(afterNode, ref)
              parent.insertBefore(chip, afterNode)
            } else {
              parent.appendChild(chip)
              parent.appendChild(afterNode)
            }
          } else if (textNode.nextSibling) {
            parent.insertBefore(afterNode, textNode.nextSibling)
            parent.insertBefore(chip, afterNode)
          } else {
            parent.appendChild(chip)
            parent.appendChild(afterNode)
          }

          // Move cursor after the space
          const newRange = document.createRange()
          newRange.setStart(afterNode, after ? 0 : 1)
          newRange.collapse(true)
          sel.removeAllRanges()
          sel.addRange(newRange)
        }
      }
    }

    setMentionOpen(false)
    setMentionQuery('')

    // Sync text value
    draftSegmentsRef.current = syncComposerSegments(el)
    isSyncing.current = true
    onChange(getTextFromEditable(el))
    isSyncing.current = false
  }, [onChange, pushUndoSnapshot, syncComposerSegments])

  /** Replace the typed `/query` with a removable skill chip. */
  const insertSkill = useCallback((skill: PromptSkill) => {
    const el = editableRef.current
    if (!el) return
    pushUndoSnapshot(true)

    const skillRef: UserMessageSegment = { type: 'skill', id: skillSlug(skill.id), name: skill.name || skill.id }
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      const textNode = range.startContainer
      if (textNode.nodeType === Node.TEXT_NODE && textNode.textContent != null) {
        const text = textNode.textContent
        const cursor = range.startOffset
        const slashIdx = text.lastIndexOf('/', cursor - 1)
        if (slashIdx >= 0) {
          const before = text.slice(0, slashIdx)
          const after = text.slice(cursor)
          textNode.textContent = before
          const afterNode = document.createTextNode(after.startsWith(' ') ? after : ` ${after}`)
          const chip = createChipNode(skillRef, handleRemoveChip)
          const parent = textNode.parentNode
          if (parent) {
            parent.insertBefore(chip, textNode.nextSibling)
            parent.insertBefore(afterNode, chip.nextSibling)
            const newRange = document.createRange()
            newRange.setStart(afterNode, Math.min(1, afterNode.textContent?.length ?? 0))
            newRange.collapse(true)
            sel.removeAllRanges()
            sel.addRange(newRange)
          }
        }
      }
    } else {
      appendSegmentNodes(el, [skillRef, { type: 'text', text: ' ' }], handleRemoveChip)
      moveCursorToEnd(el)
    }

    draftSegmentsRef.current = syncComposerSegments(el)
    isSyncing.current = true
    onChange(getTextFromEditable(el))
    isSyncing.current = false
    setIsEmpty(isTextEmpty(getTextFromEditable(el)) && !hasChipRefs(el))
    setSlashOpen(false)
    setSlashQuery('')
  }, [handleRemoveChip, onChange, pushUndoSnapshot, syncComposerSegments])

  /** Handle input events on the contentEditable — uses a native listener
   *  because React's synthetic onInput doesn't fire reliably for contentEditable. */
  useEffect(() => {
    const el = editableRef.current
    if (!el) return

    const handleNativeInput = () => {
      isSyncing.current = true
      sanitizeEditableContent(el)
      const text = getTextFromEditable(el)
      draftSegmentsRef.current = syncComposerSegments(el)
      onChangeRef.current(text)
      setIsEmpty(isTextEmpty(text) && !hasChipRefs(el))
      isSyncing.current = false
      pushUndoRef.current()

      // Detect @ trigger — resolve cursor into a text node
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      const range = sel.getRangeAt(0)
      let node: Node = range.startContainer
      let cursor = range.startOffset

      // If the cursor is in an element node, resolve to the nearest text node
      if (node.nodeType !== Node.TEXT_NODE) {
        const childAtCursor = node.childNodes[cursor - 1] ?? node.childNodes[cursor]
        if (childAtCursor?.nodeType === Node.TEXT_NODE) {
          node = childAtCursor
          cursor = (childAtCursor as Text).length
        } else if (cursor > 0 && node.childNodes[cursor - 1]) {
          let walk: Node | null = node.childNodes[cursor - 1]!
          while (walk && walk.nodeType !== Node.TEXT_NODE && walk.lastChild) {
            walk = walk.lastChild
          }
          if (walk?.nodeType === Node.TEXT_NODE) {
            node = walk
            cursor = (walk as Text).length
          } else {
            return
          }
        } else {
          return
        }
      }

      const textContent = node.textContent ?? ''
      const isOpen = mentionOpenRef.current

      if (isOpen) {
        const atIdx = textContent.lastIndexOf('@', cursor - 1)
        if (atIdx < 0 || cursor <= atIdx) {
          setMentionOpen(false)
          return
        }
        const query = textContent.slice(atIdx + 1, cursor)
        if (query.includes(' ') || query.includes('\n')) {
          setMentionOpen(false)
        } else {
          setMentionQuery(query)
        }
      } else {
        if (cursor > 0 && textContent[cursor - 1] === '@') {
          const charBefore = cursor > 1 ? textContent[cursor - 2] : ' '
          if (charBefore === ' ' || charBefore === '\n' || charBefore === '\u00a0' || cursor === 1) {
            setMentionQuery('')
            setMentionOpen(true)
          }
        }
      }

      // `/` slash-command (skills) trigger \u2014 mirrors the `@` handling above
      const slashIsOpen = slashOpenRef.current
      if (slashIsOpen) {
        const slashIdx = textContent.lastIndexOf('/', cursor - 1)
        if (slashIdx < 0 || cursor <= slashIdx) {
          setSlashOpen(false)
        } else {
          const query = textContent.slice(slashIdx + 1, cursor)
          if (query.includes(' ') || query.includes('\n') || query.includes('/')) {
            setSlashOpen(false)
          } else {
            setSlashQuery(query)
          }
        }
      } else if (cursor > 0 && textContent[cursor - 1] === '/') {
        const charBefore = cursor > 1 ? textContent[cursor - 2] : ' '
        if (charBefore === ' ' || charBefore === '\n' || charBefore === '\u00a0' || cursor === 1) {
          if (availableSkillsRef.current.length > 0) {
            setMentionOpen(false)
            setSlashQuery('')
            setSlashOpen(true)
          }
        }
      }
    }

    el.addEventListener('input', handleNativeInput)
    return () => el.removeEventListener('input', handleNativeInput)
  }, [syncComposerSegments]) // other dependencies are read from refs

  /**
   * Run one composer send action with the payload the caller already collected.
   * Returns false when no handler is wired for that action.
   */
  const runSendAction = useCallback((
    action: ComposerSendAction,
    payload: { chips: ReferencedFile[]; segments: UserMessageSegment[] },
  ) => {
    const handler =
      action === 'steer' ? onSteer
        : action === 'queue' ? onQueue
          : action === 'thread' ? onThreadSubmit
            : onAskInParallel
    if (!handler || readingAttachments > 0) return false
    handler(payload.chips, collectAttachments(attachments.filter(item => !item.mimeType.startsWith('image/')), payload.segments), payload.segments)
    setAttachments([])
    resetComposer()
    return true
  }, [attachments, onQueue, onSteer, onThreadSubmit, onAskInParallel, resetComposer, readingAttachments])

  /** Send whatever is in the composer right now through one streaming action. */
  const handleSendAction = useCallback((action: ComposerSendAction) => {
    const el = editableRef.current
    const chips = el ? getChipFiles(el) : []
    const nextSegments = el ? getComposerSegments(el) : normalizeUserMessageSegments(segments)
    runSendAction(action, { chips, segments: nextSegments })
  }, [runSendAction, segments])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Intercept undo/redo to prevent browser native undo corruption
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        const stack = undoStackRef.current
        if (stack.length === 0) return
        redoStackRef.current.push([...draftSegmentsRef.current])
        const snap = stack.pop()!
        restoreSnapshot(snap)
        return
      }
      if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault()
        const stack = redoStackRef.current
        if (stack.length === 0) return
        undoStackRef.current.push([...draftSegmentsRef.current])
        const snap = stack.pop()!
        restoreSnapshot(snap)
        return
      }
    }

    if (slashOpen && slashResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashIndex((i) => Math.min(i + 1, slashResults.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (slashResults[slashIndex]) insertSkill(slashResults[slashIndex])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSlashOpen(false)
        return
      }
    }
    if (mentionOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex((i) => Math.min(i + 1, searchResults.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (searchResults[mentionIndex]) insertMention(searchResults[mentionIndex])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionOpen(false)
        return
      }
    }
    if (e.key === 'Backspace') {
      const el = editableRef.current
      if (!el) return
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      const range = sel.getRangeAt(0)
      if (!range.collapsed) return

      const { startContainer, startOffset } = range

      // Case 1: cursor is at the root, or at the start of a direct child after a chip
      if (startContainer === el || startContainer.parentNode === el) {
        const childIndex = startContainer === el ? startOffset : Array.from(el.childNodes).indexOf(startContainer as ChildNode)
        const shouldRemove = shouldRemovePreviousChipOnBackspace({
          startContainerIsRoot: startContainer === el,
          startContainerIsText: startContainer.nodeType === Node.TEXT_NODE,
          startOffset,
          childIndex,
        })
        if (shouldRemove) {
          const prev = el.childNodes[childIndex - 1]
          if (prev instanceof HTMLElement && prev.hasAttribute('data-chip-ref')) {
            e.preventDefault()
            const nextSibling = prev.nextSibling
            pushUndoSnapshot(true)
            prev.remove()
            restoreCaretAfterChipRemovalStable(el, nextSibling, childIndex)
            draftSegmentsRef.current = syncComposerSegments(el)
            isSyncing.current = true
            onChangeRef.current(getTextFromEditable(el))
            isSyncing.current = false
            setIsEmpty(isTextEmpty(getTextFromEditable(el)) && !hasChipRefs(el))
            return
          }
        }
      }

      // Case 2: cursor is at offset 0 of a text node — check if previous sibling is a chip
      if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
        const prev = startContainer.previousSibling
        if (prev instanceof HTMLElement && prev.hasAttribute('data-chip-ref')) {
          e.preventDefault()
          const nextSibling = prev.nextSibling
          pushUndoSnapshot(true)
          prev.remove()
          restoreCaretAfterChipRemovalStable(el, nextSibling, Array.from(el.childNodes).indexOf(startContainer as ChildNode) + 1)
          draftSegmentsRef.current = syncComposerSegments(el)
          isSyncing.current = true
          onChangeRef.current(getTextFromEditable(el))
          isSyncing.current = false
          setIsEmpty(isTextEmpty(getTextFromEditable(el)) && !hasChipRefs(el))
          return
        }
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      if (readingAttachments > 0) {
        e.preventDefault()
        return
      }
      e.preventDefault()
      const el = editableRef.current
      const text = el ? getTextFromEditable(el).trim() : value.trim()
      const chips = el ? getChipFiles(el) : []
      const nextSegments = el ? getComposerSegments(el) : normalizeUserMessageSegments(segments)
      const hasStructuredRefs = nextSegments.some((segment) => segment.type !== 'text' && segment.type !== 'image')
      if (!(text || chips.length > 0 || hasStructuredRefs || attachments.length > 0)) return
      const action = resolvePromptSubmitAction({
        isLoading,
        sendTarget,
        hasQueueHandler: Boolean(onQueue),
        hasSteerHandler: Boolean(onSteer),
        hasThreadHandler: Boolean(onThreadSubmit),
        defaultAction: defaultStreamingAction,
      })
      if (isLoading && e.altKey) {
        // Alt+Enter is the secondary action: the opposite of the resolved default.
        const altAction = alternateStreamingAction(action === 'steer' ? 'steer' : 'queue')
        if (runSendAction(altAction, { chips, segments: nextSegments })) return
        // No handler for the alternate action — fall through to the default.
      }
      if (action === 'submit') {
        if (!isLoading || sendTarget === 'thread') {
          onSubmit(chips, collectAttachments(attachments.filter(item => !item.mimeType.startsWith('image/')), nextSegments), nextSegments)
          setAttachments([])
          resetComposer()
        }
        return
      }
      runSendAction(action, { chips, segments: nextSegments })
    }
  }, [mentionOpen, searchResults, mentionIndex, insertMention, slashOpen, slashResults, slashIndex, insertSkill, value, isLoading, sendTarget, defaultStreamingAction, onQueue, onSteer, onThreadSubmit, onAskInParallel, onSubmit, attachments, segments, resetComposer, restoreSnapshot, runSendAction, readingAttachments, syncComposerSegments])

  const readFileAsAttachment = useCallback((file: File): Promise<ChatAttachment> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(',')[1] ?? ''
        const isImage = file.type.startsWith('image/')
        resolve({
          name: file.name,
          mimeType: file.type || 'application/octet-stream',
          data: base64,
          preview: isImage ? result : undefined,
        })
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }, [])

  const addFilesAsAttachments = useCallback(async (files: FileList | File[]) => {
    const el = editableRef.current
    if (!el) return
    pushUndoSnapshot(true)
    setAttachmentError(null)
    // Reserve the paste position before FileReader yields, even if the user keeps typing.
    const marker = document.createElement('span')
    marker.contentEditable = 'false'
    marker.dataset.chipRef = createInlineNodeRef('pending')
    marker.className = 'mx-1 inline-flex rounded border border-border px-2 text-xs text-muted-foreground'
    marker.textContent = 'Loading attachment…'
    const selection = window.getSelection()
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null
    if (range && el.contains(range.commonAncestorContainer)) {
      range.deleteContents()
      range.insertNode(marker)
      range.setStartAfter(marker)
      range.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(range)
    } else {
      el.appendChild(marker)
    }
    setReadingAttachments(count => count + 1)
    setIsEmpty(false)
    try {
      const results = await Promise.all(Array.from(files).map(readFileAsAttachment))
      if (!el.contains(marker)) return // Draft changed or placeholder was deleted.
      const names = new Set(collectAttachments(attachmentsRef.current.filter(item => !item.mimeType.startsWith('image/')), getComposerSegments(el)).map(item => item.name))
      const added = results.map((attachment) => {
        const original = attachment.name || 'attachment'
        const dot = original.lastIndexOf('.')
        const stem = dot > 0 ? original.slice(0, dot) : original
        const extension = dot > 0 ? original.slice(dot) : ''
        let name = original
        let suffix = 2
        while (names.has(name)) name = `${stem} (${suffix++})${extension}`
        names.add(name)
        return { ...attachment, name }
      })
      for (const image of added.filter(item => item.mimeType.startsWith('image/'))) {
        marker.before(createInlineImageNode(image, handleRemoveChip))
      }
      marker.remove()
      const next = [...attachmentsRef.current, ...added.filter(item => !item.mimeType.startsWith('image/'))]
      attachmentsRef.current = next
      setAttachments(next)
      draftSegmentsRef.current = syncComposerSegments(el)
      isSyncing.current = true
      onChangeRef.current(getTextFromEditable(el))
      isSyncing.current = false
      scrollCaretIntoView(el)
    } catch {
      if (el.contains(marker)) {
        marker.remove()
        setAttachmentError('Could not read the attachment. Please try pasting it again.')
      }
    } finally {
      setReadingAttachments(count => count - 1)
      if (el.isConnected) setIsEmpty(isTextEmpty(getTextFromEditable(el)) && !hasChipRefs(el))
    }
  }, [readFileAsAttachment, handleRemoveChip, pushUndoSnapshot, syncComposerSegments])

  const removeAttachment = useCallback((name: string) => {
    setAttachments((prev) => {
      const next = prev.filter((a) => a.name !== name)
      const el = editableRef.current
      if (next.length === 0 && el && isTextEmpty(getTextFromEditable(el)) && !hasChipRefs(el)) {
        setIsEmpty(true)
      }
      return next
    })
  }, [])

  const handleCopy = useCallback((e: React.ClipboardEvent) => {
    const el = editableRef.current
    if (!el) return
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return

    // Build segments from the selected content
    const range = sel.getRangeAt(0)
    const fragment = range.cloneContents()
    const tempDiv = document.createElement('div')
    tempDiv.appendChild(fragment)

    const segments = getComposerSegments(tempDiv)

    if (segments.some(segment => segment.type !== 'text')) {
      e.preventDefault()
      e.clipboardData.setData('text/plain', serializeUserMessageSegmentsToMarkdown(segments))
      const structured = serializeUserMessageSegmentsForClipboard(segments)
      if (structured) e.clipboardData.setData(JAIT_REF_MIME, structured)
    }
  }, [])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const el = editableRef.current
    const items = e.clipboardData.items
    const imageFiles: File[] = []
    for (let i = 0; i < items.length; i++) {
      if (items[i]?.type.startsWith('image/')) {
        const file = items[i]?.getAsFile()
        if (file) imageFiles.push(file)
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault()
      void addFilesAsAttachments(imageFiles)
      return
    }
    if (!el) return
    const structured = parseUserMessageClipboardPayload(e.clipboardData.getData(JAIT_REF_MIME))
    const pastedText = e.clipboardData.getData('text/plain')
    if (structured.length === 0 && isLongPaste(pastedText)) {
      e.preventDefault()
      setAttachments(previous => [...previous, createPastedTextAttachment(pastedText, previous)])
      return
    }
    const markdownSegments = structured.length > 0 ? structured : parseUserMessageMarkdown(e.clipboardData.getData('text/plain'))
    if (markdownSegments.length > 0) {
      e.preventDefault()
      pushUndoSnapshot(true)
      insertSegmentsAtCursor(el, markdownSegments, handleRemoveChip)
      draftSegmentsRef.current = syncComposerSegments(el)
      isSyncing.current = true
      onChange(getTextFromEditable(el))
      isSyncing.current = false
      setIsEmpty(isTextEmpty(getTextFromEditable(el)) && !hasChipRefs(el))
      scrollCaretIntoView(el)
      return
    }
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    if (!text) return
    pushUndoSnapshot(true)
    insertTextAtCursor(el, text)
    draftSegmentsRef.current = syncComposerSegments(el)
    const nextValue = getTextFromEditable(el)
    isSyncing.current = true
    onChange(nextValue)
    isSyncing.current = false
    setIsEmpty(isTextEmpty(nextValue) && !hasChipRefs(el))
    scrollCaretIntoView(el)
  }, [addFilesAsAttachments, handleRemoveChip, onChange, pushUndoSnapshot, syncComposerSegments])

  // Drag-and-drop handlers
  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (dragCounter.current === 1) setDragging(true)
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) setDragging(false)
  }, [])

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
  }, [])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setDragging(false)

    const el = editableRef.current
    if (!el) return

    const appendChip = (ref: PromptChipReference) => {
      if (el.querySelector(`[data-chip-ref="${CSS.escape(getChipRefKey(ref))}"]`)) return false
      const segment: UserMessageSegment = 'type' in ref
        ? ref
        : { type: 'file', path: ref.path, name: ref.name, ...(ref.kind ? { kind: ref.kind } : {}) }
      appendSegmentNodes(el, [segment], handleRemoveChip)
      draftSegmentsRef.current = syncComposerSegments(el)
      moveCursorToEnd(el)
      isSyncing.current = true
      onChange(getTextFromEditable(el))
      isSyncing.current = false
      setIsEmpty(false)
      return true
    }

    // Handle project tree file/folder drop
    const jaitFile = e.dataTransfer.getData('text/jait-file')
    if (jaitFile) {
      try {
        const file = JSON.parse(jaitFile) as ReferencedFile
        appendChip(file)
      } catch { /* invalid JSON */ }
      return
    }

    const jaitProject = e.dataTransfer.getData(JAIT_PROJECT_REF_MIME)
    if (jaitProject) {
      try {
        const project = JSON.parse(jaitProject) as UserProjectReference
        appendChip({ type: 'project', ...project })
      } catch { /* invalid JSON */ }
      return
    }

    const jaitTerminal = e.dataTransfer.getData(JAIT_TERMINAL_REF_MIME)
    if (jaitTerminal) {
      try {
        const terminal = JSON.parse(jaitTerminal) as UserTerminalReference
        appendChip({ type: 'terminal', ...terminal })
      } catch { /* invalid JSON */ }
      return
    }

    const jaitChat = e.dataTransfer.getData(JAIT_CHAT_REF_MIME)
    if (jaitChat) {
      try {
        const chat = JSON.parse(jaitChat) as UserChatReference
        appendChip({ type: 'chat', ...chat })
      } catch { /* invalid JSON */ }
      return
    }

    // Handle native file/folder drops (e.g. from OS file explorer)
    // In Desktop 32+, File.path is no longer available with contextIsolation.
    // Use webUtils.getPathForFile() exposed via preload instead.
    const getPath = window.jaitDesktop?.getPathForFile
    if (e.dataTransfer.files?.length && getPath) {
      const nativeFiles = Array.from(e.dataTransfer.files)
      const items = e.dataTransfer.items
      let added = false
      cleanEmptyNodes(el)
      for (let i = 0; i < nativeFiles.length; i++) {
        const f = nativeFiles[i]
        if (!f) continue
        const filePath = getPath(f)
        if (!filePath) continue
        const name = filePath.split(/[\\/]/).pop() ?? filePath
        const entry = items?.[i]?.webkitGetAsEntry?.()
        const kind: 'file' | 'dir' = entry?.isDirectory ? 'dir' : (f.type === '' && f.size === 0) ? 'dir' : 'file'
        added = appendChip({ path: filePath, name, kind }) || added
      }
      if (added) {
        pushUndoRef.current(true)
      }
    } else if (e.dataTransfer.files?.length) {
      // Web browser: read files as binary attachments. Directories dragged from
      // the OS have no filesystem path here, so they can't be referenced. They
      // surface as empty pseudo-files (size 0, no type) that would otherwise
      // become broken blank attachments — filter them out and upload real files.
      const uploadable = Array.from(e.dataTransfer.files).filter((f) => !(f.size === 0 && f.type === ''))
      if (uploadable.length) {
        void addFilesAsAttachments(uploadable)
      }
    }
  }, [onChange, handleRemoveChip, addFilesAsAttachments, syncComposerSegments])

  return (
    <div
      ref={rootRef}
      data-testid="chat-composer"
      className={cn(
        'group relative z-10 flex flex-col',
        merged
          ? 'bg-transparent'
          : 'rounded-2xl border bg-background dark:bg-card focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20',
        !merged && dragging && 'ring-2 ring-primary/30 border-primary/40',
        className,
      )}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {projectDisplayName && (
        <TooltipHint side="bottom" content={projectPath?.trim() || projectDisplayName}>
        <div
          className="absolute top-2 right-3 z-10 flex items-center gap-1 text-[10px] leading-none text-muted-foreground"
        >
          <span className="block truncate max-w-[180px] pointer-events-none">
            <span className="font-semibold text-foreground/60">PROJECT</span>{' '}
            {projectDisplayName}
          </span>
          {chatId && (
            <TooltipHint side="bottom" content={`Copy chat id: ${chatId}`}>
            <button
              type="button"
              aria-label="Copy chat id"
              // The box hugs the icon exactly — no padding or extra hit ring. The
              // global `button[aria-label] { min-width: 2.5rem }` mobile rule would
              // blow this tiny inline icon up to 40px wide, so override min-width
              // to 0 to kill the dead space on either side of the icon.
              className="pointer-events-auto inline-flex shrink-0 items-center justify-center rounded p-0 !min-w-0 text-muted-foreground/40 opacity-0 transition-all hover:text-foreground hover:bg-muted/60 focus-visible:opacity-100 focus-visible:text-foreground group-hover:opacity-100 group-focus-within:opacity-100 max-md:opacity-100 max-md:text-muted-foreground/70"
              onClick={async (e) => {
                e.preventDefault()
                try {
                  await navigator.clipboard.writeText(chatId)
                  setChatIdCopied(true)
                  setTimeout(() => setChatIdCopied(false), 1200)
                } catch {
                  /* clipboard unavailable */
                }
              }}
            >
              {chatIdCopied ? <Check className="h-2.5 w-2.5 text-emerald-500" /> : <Copy className="h-2.5 w-2.5" />}
            </button>
            </TooltipHint>
          )}
        </div>
        </TooltipHint>
      )}
      {attachments.some(item => !item.mimeType.startsWith('image/')) && <AttachmentList attachments={attachments.filter(item => !item.mimeType.startsWith('image/'))} onRemove={removeAttachment} className="border-b border-border px-3 py-2" />}
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.txt,.md,.ts,.tsx,.js,.jsx,.py,.json,.yaml,.yml,.toml,.css,.html,.xml,.csv,.log,.sh,.bat,.rs,.go,.java,.c,.cpp,.h,.hpp"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) {
            void addFilesAsAttachments(e.target.files)
            e.target.value = ''
          }
        }}
      />
      {attachmentError && <p role="alert" className="px-3 pt-2 text-xs text-destructive">{attachmentError}</p>}
      {/* Editable area with inline references and images */}
      <div className="relative px-3 pt-3 pb-1.5">
        {isEmpty && (
          <div className="absolute top-5 left-5 pointer-events-none text-muted-foreground leading-relaxed select-none [font-size:0.9rem]">
            {placeholder}
          </div>
        )}
        <div
          ref={editableRef}
          contentEditable={!composerDisabled}
          role="textbox"
          aria-multiline
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onCopy={handleCopy}
          onDrop={(e) => { e.preventDefault() }}
          onDragStart={(e) => { e.preventDefault() }}
          onBeforeInput={(e) => {
            const inputType = (e.nativeEvent as InputEvent).inputType
            if (inputType === 'insertFromDrop' || inputType === 'insertHTML') e.preventDefault()
          }}
          className={cn(
            'min-h-[40px] max-h-[200px] overflow-y-auto leading-relaxed outline-none py-2 px-2 text-foreground [font-size:0.9rem]',
            'whitespace-pre-wrap break-words',
            composerDisabled && 'cursor-not-allowed opacity-50',
          )}
          style={{ wordBreak: 'break-word' }}
        />
      </div>

      {/* @ mention popup */}
      <Popover open={mentionOpen}>
        <PopoverAnchor asChild>
          <span aria-hidden className="pointer-events-none absolute left-3 top-0 h-0 w-0" />
        </PopoverAnchor>
        <PopoverContent
          ref={menuRef}
          role="listbox"
          aria-label="File suggestions"
          side="top"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-[80] w-72 max-h-52 overflow-y-auto rounded-lg p-1 shadow-lg"
        >
          {searchResults.map((file, i) => (
            <button
              key={file.path}
              type="button"
              role="option"
              aria-selected={i === mentionIndex}
              data-active={i === mentionIndex}
              className={cn(
                'flex items-center gap-2 w-full text-left text-xs px-2 py-1.5 rounded-md transition-colors',
                i === mentionIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
              )}
              onMouseDown={(e) => {
                e.preventDefault()
                insertMention(file)
              }}
              onMouseEnter={() => setMentionIndex(i)}
            >
              {file.kind === 'dir'
                ? <FolderIcon name={file.name} className="h-3.5 w-3.5 shrink-0" />
                : <FileIcon filename={file.name} className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate">{file.path}</span>
            </button>
          ))}
          {searchLoading && (
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Searching...
            </div>
          )}
          {!searchLoading && searchResults.length === 0 && (
            <div className="text-xs text-muted-foreground px-2 py-1.5">
              {projectOpen ? 'No matching files or folders' : 'Open a project to mention files'}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* `/` slash-command (skills) popup */}
      <Popover open={slashOpen}>
        <PopoverAnchor asChild>
          <span aria-hidden className="pointer-events-none absolute left-3 top-0 h-0 w-0" />
        </PopoverAnchor>
        <PopoverContent
          ref={slashMenuRef}
          role="listbox"
          aria-label="Skill suggestions"
          side="top"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-[80] w-80 max-h-60 overflow-y-auto rounded-lg p-1 shadow-lg"
        >
          <div className="px-2 py-1 text-2xs font-medium uppercase tracking-wide text-muted-foreground/70">
            Skills
          </div>
          {slashResults.map((skill, i) => (
            <button
              key={skill.id}
              type="button"
              role="option"
              aria-selected={i === slashIndex}
              data-active={i === slashIndex}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors',
                i === slashIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
              )}
              onMouseDown={(e) => {
                e.preventDefault()
                insertSkill(skill)
              }}
              onMouseEnter={() => setSlashIndex(i)}
            >
              <span className="flex w-full items-center gap-1.5 text-xs font-medium">
                <span className="text-muted-foreground">/</span>
                <span className="truncate">{skill.name}</span>
              </span>
              {skill.description && (
                <span className="line-clamp-1 text-2xs text-muted-foreground">{skill.description}</span>
              )}
            </button>
          ))}
          {slashResults.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">No matching skills</div>
          )}
        </PopoverContent>
      </Popover>

      <div className="flex min-w-0 items-start gap-2 px-3 pb-2.5 pt-0.5">
        {hasFooterLeftContent && (
          <div className="min-w-0 flex-1">
            {hasFooterControls && (
              <div className="min-w-0">
                <div className="min-w-0 overflow-x-auto scrollbar-none">
                  <div className="flex min-w-max items-center gap-1 pr-1">
                    {footerLeadingContent}
                    {showResponseStyleSelector && (
                      <StyleSelector
                        value={responseStyle!}
                        onChange={onResponseStyleChange!}
                        disabled={selectorsDisabled}
                        compact={compactFooterControls}
                      />
                    )}
                    {showProviderModelSelector && (
                      <ProviderModelSelector
                        provider={provider!}
                        model={cliModel ?? null}
                        onProviderChange={onProviderChange!}
                        onModelChange={onCliModelChange!}
                        disabled={selectorsDisabled}
                        compact={compactFooterControls}
                        repoRuntime={repoRuntime}
                        onMoveToGateway={onMoveToGateway}
                        sessionInfo={sessionInfo}
                        projectNodeId={projectNodeId}
                        projectId={projectId}
                        reasoningEffort={reasoningEffort}
                        onReasoningEffortChange={onReasoningEffortChange}
                        tooltipSide="bottom"
                      />
                    )}
                    {showProviderRuntimeSelector && (
                      <ProviderRuntimeSelector
                        provider={provider!}
                        value={providerRuntimeMode!}
                        onChange={onProviderRuntimeModeChange!}
                        disabled={selectorsDisabled}
                        compact={compactFooterControls}
                      />
                    )}
                    {showModeSelector && (
                      <ModeSelector mode={mode!} onChange={onModeChange!} disabled={selectorsDisabled} compact={compactFooterControls} />
                    )}
                  </div>
                </div>
                {shouldShowSendTargetSelector && (
                  <div className="mt-1.5">
                    <SendTargetSelector
                      target={sendTarget!}
                      onChange={onSendTargetChange!}
                      disabled={selectorsDisabled}
                      compact={compactFooterControls}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className={cn('flex shrink-0 items-center gap-1.5', hasFooterLeftContent ? 'pl-1' : 'ml-auto')}>
          <TooltipHint side="bottom" content="Attach files">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="shrink-0 rounded-lg"
            onClick={() => fileInputRef.current?.click()} aria-label="Attach files"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          </TooltipHint>
          {onVoiceInput && !voiceRecording && !voiceTranscribing && (
            <TooltipHint side="bottom" content="Voice input">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="shrink-0 rounded-lg"
              onClick={onVoiceInput} aria-label="Voice input"
            >
              <Mic className="h-4 w-4" />
            </Button>
            </TooltipHint>
          )}
          {voiceRecording && onVoiceStop && (
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-foreground/80">
                <span className="h-2 w-2 rounded-full bg-foreground/70 animate-pulse" />
                {!isMobile && 'Listening...'}
              </span>
              <VoiceLevelMeter levels={voiceLevels} compact={isMobile} />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={cn('h-7 shrink-0 rounded-lg text-xs', isMobile ? 'px-2' : 'px-2.5')}
                onClick={onVoiceStop}
              >
                <MicOff className={cn('h-3.5 w-3.5', !isMobile && 'mr-1')} />
                {!isMobile && 'Done'}
              </Button>
            </div>
          )}
          {voiceTranscribing && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Transcribing...
            </div>
          )}
          {footerTrailingContent}
          {isLoading && sendTarget !== 'thread' && (
            <TooltipHint side="bottom" content="Stop generating">
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="shrink-0 rounded-lg"
              onClick={onStop} aria-label="Stop generating"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </Button>
            </TooltipHint>
          )}
          {isLoading && sendTarget !== 'thread' ? (
            submitEmpty || !hasStreamingSendAction ? (
              <TooltipHint side="bottom" content="Sending">
              <Button
                type="button"
                size="icon"
                className="shrink-0 rounded-lg"
                disabled aria-label="Sending"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
              </Button>
              </TooltipHint>
            ) : (
              <ComposerSendSplitButton
                primary={streamingSendPrimary}
                options={streamingSendOptions}
                disabled={composerDisabled}
                loading={Boolean(submitLoading)}
                onAction={handleSendAction}
              />
            )
          ) : !isLoading || sendTarget === 'thread' ? (
            <TooltipHint side="bottom" content={submitLoading ? 'Loading chat' : undefined}>
            <Button
              type="button"
              size="icon"
              className="shrink-0 rounded-lg"
              disabled={Boolean(submitLoading) || submitEmpty || composerDisabled}
              aria-label="Send message"
              onClick={() => {
                const el = editableRef.current
                const chips = el ? getChipFiles(el) : []
                const nextSegments = el ? getComposerSegments(el) : normalizeUserMessageSegments(segments)
                onSubmit(chips, collectAttachments(attachments.filter(item => !item.mimeType.startsWith('image/')), nextSegments), nextSegments)
                setAttachments([])
                resetComposer()
              }}
            >
              {submitLoading
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <ArrowUp className="h-4 w-4" />}
            </Button>
            </TooltipHint>
          ) : (
            <TooltipHint side="bottom" content="Sending">
            <Button
              type="button"
              size="icon"
              className="shrink-0 rounded-lg"
              disabled aria-label="Sending"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
            </Button>
            </TooltipHint>
          )}
        </div>
      </div>
    </div>
  )
})
